import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static targets = [
    "button",
    "panel",
    "badge",
    "badgeCount",
    "bellIcon",
    "list",
    "loading",
    "empty",
    "markAllButton"
  ]

  static values = {
    url: String,
    markReadUrl: String,
    markAllReadUrl: String,
    pollingInterval: { type: Number, default: 30000 }
  }

  connect() {
    this.fetchNotifications()
    this.startPolling()
    this.bindKeyboardNavigation()
    this.bindOutsideClick()
    this.subscribeToNotifications()
  }

  disconnect() {
    this.stopPolling()
    this.unbindOutsideClick()
    this.unsubscribeFromNotifications()
  }

  subscribeToNotifications() {
    this.channel = consumer.subscriptions.create("NotificationsChannel", {
      connected: () => {
        console.log("Connected to NotificationsChannel")
      },

      disconnected: () => {
        console.log("Disconnected from NotificationsChannel")
      },

      received: (data) => {
        const { notification, unread_count } = data

        // Update badge count immediately
        if (unread_count !== undefined) {
          this.updateBadge(unread_count)
        }

        // Show modal if notification requires it
        if (notification && notification.modal) {
          this.showNotificationModal(notification)
        }

        // Refresh the full notification list
        this.fetchNotifications()
      }
    })
  }

  showNotificationModal(notification) {
    const modal = document.querySelector("#notification-modal")
    if (!modal) return

    const titleTarget = modal.querySelector('[data-notification-modal-target="title"]')
    const timestampTarget = modal.querySelector('[data-notification-modal-target="timestamp"]')
    const messageTarget = modal.querySelector('[data-notification-modal-target="message"]')

    if (titleTarget) titleTarget.textContent = notification.title
    if (timestampTarget) timestampTarget.textContent = notification.time_ago
    if (messageTarget) messageTarget.textContent = notification.message

    if (notification.url) {
      const actionLink = modal.querySelector('[data-notification-modal-target="actionLink"]')
      if (actionLink) actionLink.href = notification.url
    }

    // Try to use modal controller if available, otherwise show modal directly
    const modalElement = modal.closest("[data-controller*='modal']")
    if (modalElement) {
      const modalController = this.application?.getControllerForElementAndIdentifier(modalElement, "modal")
      if (modalController && typeof modalController.open === "function") {
        modalController.open()
        return
      }
    }

    // Fallback: show modal directly
    modal.classList.remove("hidden")
    modal.setAttribute("aria-hidden", "false")
    document.body.style.overflow = "hidden"
  }

  unsubscribeFromNotifications() {
    if (this.channel) {
      this.channel.unsubscribe()
    }
  }

  toggle(event) {
    event.preventDefault()
    const isOpen = !this.panelTarget.classList.contains("hidden")

    if (isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    this.panelTarget.classList.remove("hidden")
    this.buttonTarget.setAttribute("aria-expanded", "true")
    this.fetchNotifications()
  }

  close() {
    this.panelTarget.classList.add("hidden")
    this.buttonTarget.setAttribute("aria-expanded", "false")
  }

  async fetchNotifications() {
    if (!this.hasUrlValue) return

    this.showLoading()

    try {
      const response = await fetch(this.urlValue)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      this.updateNotifications(data)
    } catch (error) {
      console.error("Error fetching notifications:", error)
      this.showError()
    } finally {
      this.hideLoading()
    }
  }

  updateNotifications(data) {
    const { notifications, unread_count } = data

    this.updateBadge(unread_count)

    if (notifications.length === 0) {
      this.showEmpty()
    } else {
      this.renderNotifications(notifications)
      this.hideEmpty()
    }

    if (this.hasMarkAllButtonTarget) {
      this.markAllButtonTarget.disabled = unread_count === 0
    }
  }

  renderNotifications(notifications) {
    if (!this.hasListTarget) return

    this.listTarget.innerHTML = notifications.map(notification =>
      this.createNotificationHTML(notification)
    ).join("")
  }

  createNotificationHTML(notification) {
    const isUnread = !notification.read
    const unreadDot = isUnread
      ? '<span class="flex-shrink-0 w-2 h-2 rounded-full bg-blue-600" aria-label="Non lu"></span>'
      : ''

    // Bouton de téléchargement si fichier attaché
    const downloadButton = notification.file_url
      ? `<a href="${notification.file_url}"
            class="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors"
            data-action="click->notifications#stopPropagation">
            <i class="fa-solid fa-download"></i>
            Télécharger
         </a>`
      : ''

    return `
      <div class="notification-item ${isUnread ? 'bg-blue-50/30' : ''} hover:bg-gray-50 transition-colors duration-200"
           data-notification-id="${notification.id}">

        <button type="button"
                data-action="click->notifications#markAsRead"
                data-notification-id="${notification.id}"
                ${notification.url ? `data-notification-url="${notification.url}"` : ''}
                ${notification.file_url ? `data-notification-url="${notification.file_url}"` : ''}
                class="w-full px-4 py-3 flex items-start gap-3 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-400">

          <div class="flex-shrink-0 mt-0.5">
            ${this.getNotificationIcon(notification.notification_type)}
          </div>

          <div class="flex-1 min-w-0">
            <div class="flex items-start justify-between gap-2 mb-1">
              <p class="text-sm font-medium text-gray-900 line-clamp-1">
                ${this.escapeHtml(notification.title)}
              </p>
              ${unreadDot}
            </div>

            <p class="text-sm text-gray-600 line-clamp-2 mb-1">
              ${this.escapeHtml(notification.message)}
            </p>

            <p class="text-xs text-gray-500">
              ${notification.time_ago}
            </p>

            ${downloadButton}
          </div>
        </button>
      </div>
    `
  }

  // Empêche le clic sur le lien de téléchargement de déclencher markAsRead sur le bouton parent
  stopPropagation(event) {
    event.stopPropagation()
  }

  getNotificationIcon(type) {
    const icons = {
      message: { icon: "fas fa-envelope", bg: "bg-purple-100", color: "text-purple-600" },
      promotion: { icon: "fas fa-book-reader", bg: "bg-blue-100", color: "text-blue-600" },
      evaluation: { icon: "fas fa-tasks", bg: "bg-green-100", color: "text-green-600" },
      attendance: { icon: "fas fa-calendar-check", bg: "bg-orange-100", color: "text-orange-600" },
      system: { icon: "fas fa-bell", bg: "bg-gray-100", color: "text-gray-600" }
    }

    const config = icons[type] || icons.system

    return `
      <div class="flex items-center justify-center w-10 h-10 rounded-lg ${config.bg}">
        <i class="${config.icon} ${config.color} text-base"></i>
      </div>
    `
  }

  updateBadge(count) {
    if (!this.hasBadgeTarget) return

    if (count > 0) {
      this.badgeTarget.classList.remove("hidden")
      this.badgeCountTarget.textContent = count > 99 ? "99+" : count
      this.bellIconTarget.classList.add("text-red-500")
      this.bellIconTarget.classList.remove("text-gray-700")
    } else {
      this.badgeTarget.classList.add("hidden")
      this.bellIconTarget.classList.remove("text-red-500")
      this.bellIconTarget.classList.add("text-gray-700")
    }
  }

  async markAsRead(event) {
    event.preventDefault()
    const button = event.currentTarget
    const notificationId = button.dataset.notificationId
    const notificationUrl = button.dataset.notificationUrl

    if (!notificationId) return

    try {
      const url = this.markReadUrlValue.replace(':id', notificationId)
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCSRFToken()
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const notificationElement = this.findNotificationElement(notificationId)
      if (notificationElement) {
        notificationElement.classList.remove("bg-blue-50/30")
        const unreadDot = notificationElement.querySelector('[aria-label="Non lu"]')
        if (unreadDot) {
          unreadDot.remove()
        }
      }

      await this.fetchNotifications()

      if (notificationUrl) {
        window.location.href = notificationUrl
      }
    } catch (error) {
      console.error("Error marking notification as read:", error)
    }
  }

  async markAllRead(event) {
    event.preventDefault()

    if (!this.hasMarkAllReadUrlValue) return

    try {
      const response = await fetch(this.markAllReadUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCSRFToken()
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      await this.fetchNotifications()
    } catch (error) {
      console.error("Error marking all as read:", error)
    }
  }

  startPolling() {
    if (this.pollingIntervalValue > 0) {
      this.pollingTimer = setInterval(() => {
        this.fetchNotifications()
      }, this.pollingIntervalValue)
    }
  }

  stopPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer)
    }
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
    }
    if (this.hasListTarget) {
      this.listTarget.classList.add("hidden")
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
    }
    if (this.hasListTarget) {
      this.listTarget.classList.remove("hidden")
    }
  }

  showEmpty() {
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.remove("hidden")
    }
    if (this.hasListTarget) {
      this.listTarget.classList.add("hidden")
    }
  }

  hideEmpty() {
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.add("hidden")
    }
    if (this.hasListTarget) {
      this.listTarget.classList.remove("hidden")
    }
  }

  showError() {
    if (this.hasListTarget) {
      this.listTarget.innerHTML = `
        <div class="flex flex-col items-center justify-center py-8 px-4">
          <i class="fas fa-exclamation-triangle text-3xl text-red-500 mb-3"></i>
          <p class="text-sm text-gray-600 text-center">Erreur lors du chargement des notifications</p>
        </div>
      `
    }
  }

  bindKeyboardNavigation() {
    this.element.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        this.close()
      }
    })
  }

  bindOutsideClick() {
    this.outsideClickHandler = (event) => {
      const isOpen = !this.panelTarget.classList.contains("hidden")
      const clickedInsidePanel = this.panelTarget.contains(event.target)
      const clickedOnButton = this.buttonTarget.contains(event.target)

      if (isOpen && !clickedInsidePanel && !clickedOnButton) {
        this.close()
      }
    }
    document.addEventListener("click", this.outsideClickHandler)
  }

  unbindOutsideClick() {
    if (this.outsideClickHandler) {
      document.removeEventListener("click", this.outsideClickHandler)
    }
  }

  findNotificationElement(notificationId) {
    return this.element.querySelector(`[data-notification-id="${notificationId}"]`)
  }

  getCSRFToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
